import React from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Input } from '../ui/Input/Input';
import { Button } from '../ui/Button/Button';
import { Alert } from '../ui/Alert/Alert';
import { Checkbox } from '../ui/Checkbox/Checkbox';

export function AuthPage() {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const location = useLocation() as unknown as { state?: { from?: string } };

  React.useEffect(() => {
    if (user) {
      
      // Check if we need to redirect to onboarding for new registrations
      const checkOnboarding = async () => {
        try {
          // Check for new registration flag
          const redirectOnboarding = localStorage.getItem('ima_redirect_onboarding') === '1';
          
          
          if (redirectOnboarding) {
            
            localStorage.removeItem('ima_redirect_onboarding');
            navigate('/onboarding', { replace: true });
            return;
          }

          // Use org data from store instead of making API call
          // Organization data is now initialized from bootstrap in the router loader
          const { useOrgStore } = await import('@/stores/orgStore');
          const orgState = useOrgStore.getState();
          const orgs = orgState.orgs;
          const activeId = orgState.activeOrgId;
          const active = orgs.find((o: any) => o.id === activeId) || orgs[0];
          
          if (active) {
            let completed = Boolean(active.onboarding_completed);
            if (!completed) {
              try { completed = localStorage.getItem(`ima_onboarding_completed_${active.id}`) === '1'; } catch {}
            }
            const destination = completed ? '/dashboard' : '/onboarding';
            navigate(destination, { replace: true });
            return;
          }
        } catch (e) {
          
        }
        
        // Fallback
        const from = (location?.state?.from as string | undefined) || '/dashboard';
        
        navigate(from, { replace: true });
      };

      checkOnboarding();
    }
  }, [user, navigate, location]);

  if (user) {
    // Avoid rendering the auth UI at all if already authenticated
    return null;
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <img 
              src="/images/logo_moniker_cockpit.png" 
              alt="Moniker Cockpit" 
              className="h-8 mx-auto mb-4 object-contain"
            />
            <p className="text-sm text-muted-foreground mt-1">Company Automation Platform</p>
          </Link>
        </div>

        {/* Auth Card */}
        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground">Sign In</h2>
              <p className="text-sm text-muted-foreground mt-1">Access your account</p>
            </div>
            <LoginForm />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-muted-foreground">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </div>
      </div>
    </div>
  );
}

function LoginForm() {
  const login = useAuthStore(s => s.login);
  const isLoading = useAuthStore(s => s.isLoading);
  const error = useAuthStore(s => s.error);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [remember, setRemember] = React.useState(true);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(email, password, remember);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="space-y-4">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="••••••••"
          required
        />
      </div>
      
      <div className="flex items-center justify-between">
        <Checkbox
          checked={remember}
          onChange={e => setRemember(e.target.checked)}
          label="Remember me"
        />
        <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">Forgot password?</Link>
      </div>
      
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}
      
      <Button
        type="submit"
        variant="primary"
        className="w-full"
        loading={isLoading}
      >
        Sign in
      </Button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t"></div>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
          <path fill="#34A853" d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96h-3.98v3.09C3.515 21.3 7.565 24 12.255 24z"/>
          <path fill="#FBBC05" d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62h-3.98a11.86 11.86 0 000 10.76l3.98-3.09z"/>
          <path fill="#EA4335" d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C18.205 1.19 15.495 0 12.255 0c-4.69 0-8.74 2.7-10.71 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"/>
        </svg>
        Google (Coming soon)
      </Button>
    </form>
  );
}
